"use client";

import { useMemo } from "react";
import ItemCard from "@/components/ui/ItemCard";
import { Item } from "@/lib/types";

interface RecentlySoldItemsProps {
  items: Item[];
  title: string;
}

/**
 * Displays recently sold items sorted by date (newest first).
 * No distance calculation — sold items are shown for awareness, not for purchase proximity.
 */
export default function RecentlySoldItems({
  items,
  title,
}: RecentlySoldItemsProps) {
  const displayedItems = useMemo(() => {
    return [...items]
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )
      .slice(0, 12);
  }, [items]);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">{title}</h2>
      </div>

      {displayedItems.length > 0 ? (
        <div className="relative">
          <div className="overflow-x-auto pb-4 hide-scrollbar">
            <div className="flex gap-6" style={{ minWidth: "min-content" }}>
              {displayedItems.map((item, index) => (
                <div key={item.id} className="relative w-[280px] flex-shrink-0">
                  <ItemCard
                    item={item}
                    distance={undefined}
                    index={index}
                    priority={index < 6}
                    disableLink={true}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-10 bg-secondary rounded-lg">
          <p className="text-text-light">No recently sold items available.</p>
        </div>
      )}
    </div>
  );
}
